import { Component, ErrorInfo, ReactNode } from 'react'
import Button from './Button'
import i18n from '@/i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div
          className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center"
          style={{ background: '#050510' }}
        >
          <div className="text-5xl mb-4">⚠</div>
          <h2 className="text-text font-bold text-xl mb-2">{i18n.t('error.pageError')}</h2>
          <p className="text-text-muted text-sm mb-6 max-w-xs">
            {i18n.t('error.unexpectedError')}
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre
              className="p-4 rounded-xl text-left text-xs mb-6 max-w-sm overflow-auto w-full"
              style={{ background: '#0C0C1C', color: '#A1A1AA', border: '1px solid #1A1A38' }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-3">
            <Button onClick={this.handleReset}>{i18n.t('error.retry')}</Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              {i18n.t('error.refreshPage')}
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
